/* global module:true */
import 'reflect-metadata';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import Cookies from 'cookies-js';
import { CacheProvider } from '@emotion/react';
import { loadableReady } from '@loadable/component';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';
import { SvgIconCacheProvider } from '@cp/ds/src/components/svgIcon/components/cache/provider';
import { Router, useHistory } from 'react-router-dom';
import { REACT_APP_CONTAINER_ID, REACT_APP_PRELOADED_STATE_CONTAINER_ID, REACT_APP_SSR_STATE_CONTAINER_ID } from '@common/const/reactApp';
import { CLIENT_APP_EXCEPTION_FALLBACK_HTML } from '@common/exception/fallback';
import { ReactAppSSRState } from '@common/model/reactApp/ssrState';
import { bootstrap } from 'src/app/bootstrap';
import { IocContainerProvider, container } from 'src/app/providers/container';
import { RUNTIME_CONFIG_CONTAINER_ID } from 'src/app/providers/config/const';
import { HISTORY_API } from 'src/app/providers/router';
import { STORE, createReduxPersistConfig } from './app/redux';
import { App } from './app';
import reportWebVitals from './reportWebVitals';
import { createEmotionCache } from './createEmotionCache';
import { getSsrMatchMedia } from './ssrMatchMedia';
import { ErrorBoundary } from './app/shared/components/errorBoundary';
import { ProvidersProps } from './app/providers';
import { initializeEmbedding } from './app/providers/embedding/model';
import { clientSideGetSSRVarioqubValue } from './app/providers/varioqub';

const ErrorBoundaryWrapped: React.FC<React.PropsWithChildren<React.ComponentProps<typeof ErrorBoundary>>> = (props) => {
  const history = useHistory();
  return <ErrorBoundary {...props} onRedirect={history.replace} />;
};

const main = () => {
  const rootElement = document.getElementById(REACT_APP_CONTAINER_ID) as Element;
  const preloadedState = window[REACT_APP_PRELOADED_STATE_CONTAINER_ID];
  const ssrState = window[REACT_APP_SSR_STATE_CONTAINER_ID] as unknown as ReactAppSSRState;

  const runtimeConfigContainer = document.getElementById(RUNTIME_CONFIG_CONTAINER_ID);
  const runtimeConfig = JSON.parse(runtimeConfigContainer?.textContent || '{}');
  const varioqub = clientSideGetSSRVarioqubValue();

  Cookies.defaults.expires = Infinity;
  if (runtimeConfig.CLIENT_WEB_ORIGIN) {
    Cookies.defaults.domain = new URL(runtimeConfig.CLIENT_WEB_ORIGIN).hostname;
  }
  const persistConfig = createReduxPersistConfig({ cookiesAdapter: Cookies });

  bootstrap(container, {
    config: runtimeConfig,
    redux: {
      persistConfig,
      preloadedState,
    },
    varioqub,
  });
  const store = container.get(STORE);

  initializeEmbedding({ store });
  persistStore(store);

  document.getElementById(REACT_APP_PRELOADED_STATE_CONTAINER_ID)?.remove();

  const cache = createEmotionCache();
  const ssrMatchMedia = getSsrMatchMedia(ssrState.deviceType ?? 'desktop');
  const providersProps: ProvidersProps = {
    ssrMatchMedia,
  };

  try {
    const hydrateApp = () => {
      hydrateRoot(
        rootElement,
        <React.StrictMode>
          <IocContainerProvider container={container}>
            <Provider store={store}>
              <CacheProvider value={cache}>
                <SvgIconCacheProvider>
                  <Router history={container.get(HISTORY_API)}>
                    <ErrorBoundaryWrapped {...providersProps} error={ssrState.error}>
                      <App {...providersProps} />
                    </ErrorBoundaryWrapped>
                  </Router>
                </SvgIconCacheProvider>
              </CacheProvider>
            </Provider>
          </IocContainerProvider>
        </React.StrictMode>,
      );
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(hydrateApp, { timeout: 1000 });
    } else {
      setTimeout(hydrateApp, 0);
    }
  } catch {
    rootElement.innerHTML = CLIENT_APP_EXCEPTION_FALLBACK_HTML;
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => loadableReady(main));
} else {
  loadableReady(main);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

if (module.hot) {
  module.hot.accept();
}
